import React, { useState, useMemo, useCallback, useEffect } from 'react';
import Pagination from '../Pagination';

const SelectCustomerModal = ({ isOpen, onClose, customers, onSelect, onAddNew }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(5);

    const filteredCustomers = useMemo(() => {
        if (!customers) return [];
        if (searchTerm === '') return customers;
        
        return customers.filter(customer =>
            customer.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
    }, [customers, searchTerm]);

    const paginatedCustomers = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return filteredCustomers.slice(startIndex, startIndex + itemsPerPage);
    }, [filteredCustomers, currentPage, itemsPerPage]);

    const handleItemsPerPageChange = useCallback((value) => {
        setItemsPerPage(value);
        setCurrentPage(1);
    }, []);

    useEffect(() => {
        if (!isOpen) {
            setSearchTerm('');
            setCurrentPage(1);
            setItemsPerPage(5);
        }
    }, [isOpen]);

    if (!isOpen) {
        return null;
    }

    const handleSelectCustomer = (customer) => {
        onSelect(customer);
    };

    return (
        <div className="modal-overlay">
            <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8 w-full max-w-2xl m-4 sm:m-8 max-h-[90vh] flex flex-col">
                <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4 mb-6">
                    <h2 className="text-2xl font-bold text-gray-800">Selecionar Cliente</h2>
                    <button 
                        onClick={onAddNew} 
                        className="custom-button text-sm font-semibold py-2 px-4 rounded-lg shadow-sm w-full sm:w-auto"
                    >
                        + Adicionar Novo Cliente
                    </button>
                </div>

                <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => {
                        setSearchTerm(e.target.value);
                        setCurrentPage(1);
                    }}
                    placeholder="🔎 Pesquisar por nome..."
                    className="w-full px-4 py-2 bg-white border border-[#D7BFBD] rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-[#D7BFBD]"
                    autoFocus
                />

                <div className="flex-grow overflow-y-auto">
                    {/* START: Mobile View (Card List) */}
                    <div className="space-y-3 md:hidden">
                        {paginatedCustomers.length > 0 ? paginatedCustomers.map(customer => (
                            <div key={customer.id} className="border rounded-lg p-4 flex justify-between items-center">
                                <div>
                                    <p className="font-bold text-gray-800">{customer.name}</p>
                                    <p className="text-sm text-gray-500">{customer.phone || 'N/A'}</p>
                                </div>
                                <button 
                                    onClick={() => handleSelectCustomer(customer)}
                                    className="custom-button text-xs font-bold py-2 px-4 rounded-md flex-shrink-0 ml-4"
                                >
                                    Selecionar
                                </button>
                            </div>
                        )) : (
                            <p className="p-4 text-center text-gray-500">Nenhum cliente encontrado.</p>
                        )}
                    </div>
                    {/* END: Mobile View */}
                
                    {/* START: Desktop View (Table) */}
                    <div className="overflow-x-auto hidden md:block">
                        <table className="w-full text-left">
                            <thead className="border-b-2 border-[#F2DFDB]">
                                <tr>
                                    <th className="p-4 text-sm font-semibold text-gray-700">Nome</th>
                                    <th className="p-4 text-sm font-semibold text-gray-700">Telefone</th>
                                    <th className="p-4 text-sm font-semibold text-gray-700 text-center">Ação</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedCustomers.length > 0 ? paginatedCustomers.map(customer => (
                                    <tr key={customer.id} className="border-b border-[#F2DFDB] hover:bg-[#F4EFEC]">
                                        <td className="p-4 font-medium text-gray-800">{customer.name}</td>
                                        <td className="p-4 text-gray-600">{customer.phone || 'N/A'}</td>
                                        <td className="p-4 text-center">
                                            <button 
                                                onClick={() => handleSelectCustomer(customer)}
                                                className="custom-button text-xs font-bold py-1 px-3 rounded-md"
                                            >
                                                Selecionar
                                            </button>
                                        </td>
                                    </tr>
                                )) : (
                                    <tr>
                                        <td colSpan="3" className="p-4 text-center text-gray-500">Nenhum cliente encontrado.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    {/* END: Desktop View */}
                </div>

                <div className="pt-4 mt-auto">
                    <Pagination 
                        totalItems={filteredCustomers.length}
                        itemsPerPage={itemsPerPage}
                        currentPage={currentPage}
                        onPageChange={setCurrentPage}
                        onItemsPerPageChange={handleItemsPerPageChange}
                    />

                     <div className="flex justify-end gap-4 pt-6 mt-4 border-t border-gray-200">
                        <button 
                            type="button" 
                            onClick={onClose} 
                            className="py-2 px-6 bg-gray-200 text-gray-800 font-semibold rounded-lg hover:bg-gray-300 w-full sm:w-auto"
                        >
                            Fechar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SelectCustomerModal;
